import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SENSORS_URL } from '../Constants';
import './Switch.css';

const parseBoolean = (rawValue, fallback = false) => {
  if (typeof rawValue === 'boolean') {
    return rawValue;
  }

  if (rawValue === null || rawValue === undefined) {
    return fallback;
  }

  if (typeof rawValue === 'number') {
    return rawValue !== 0;
  }

  if (typeof rawValue === 'string') {
    const normalized = rawValue.trim().toLowerCase();
    if (!normalized) {
      return fallback;
    }
    if (['false', '0', 'no', 'off'].includes(normalized)) {
      return false;
    }
    if (['true', '1', 'yes', 'on'].includes(normalized)) {
      return true;
    }
  }

  return Boolean(rawValue);
};

const normalizeLabel = (value, fallback) => {
  if (value === null || value === undefined) {
    return fallback;
  }

  const normalized = String(value);
  return normalized.trim() === '' ? fallback : normalized;
};

export const CustomSwitch = ({ checked, onChange, disabled = false }) => {
  const handleClick = () => {
    if (disabled) return;
    if (onChange) onChange(!checked);
  };

  const trackFill = checked
    ? 'var(--switch-track-checked)'
    : 'var(--switch-track-unchecked)';

  const circleCx = checked ? 42 : 14;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className="switch-widget__toggle"
      aria-pressed={checked}
      aria-disabled={disabled}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="56"
        height="28"
        viewBox="0 0 56 28"
        fill="none"
        className="switch-widget__toggle-svg"
      >
        <rect width="56" height="28" rx="14" fill={trackFill} />
        <circle
          cx={circleCx}
          cy="14"
          r="11"
          fill={disabled ? 'var(--switch-thumb-color-disabled)' : 'var(--switch-thumb-color)'}
          style={{
            filter: disabled
              ? 'none'
              : 'drop-shadow(var(--switch-thumb-shadow, 2px 0 2px rgba(0,0,0,0.35)))'
          }}
        />
      </svg>
    </button>
  );
};

const Switch = ({
  title,
  transparentBackground,
  value,
  color,
  inactiveColor,
  activeBackgroundColor,
  inactiveBackgroundColor,
  offColor,
  alignment,
  showLabel,
  onLabel,
  offLabel,
  draggable,
  pin,
  did,
  tid
}) => {
  const [check, setCheck] = useState(false);
  const [cursorState, setCursorState] = useState('idle');
  const containerRef = useRef(null);
  const shouldShowLabel = parseBoolean(showLabel, true);
  const resolvedOnLabel = normalizeLabel(onLabel, 'On');
  const resolvedOffLabel = normalizeLabel(offLabel, 'Off');

  const activeTrackColor = useMemo(
    () => color || activeBackgroundColor || null,
    [color, activeBackgroundColor]
  );

  const inactiveTrackColor = useMemo(
    () => inactiveColor || inactiveBackgroundColor || offColor || null,
    [inactiveColor, inactiveBackgroundColor, offColor]
  );

  useEffect(() => setCheck(parseFloat(value) !== 0), [value]);

  useEffect(() => {
    if (!containerRef.current) return;
    const a = (alignment != null && String(alignment).trim() !== '') ? String(alignment) : 'center';
    containerRef.current.style.setProperty('--switch-align-self', a);
  }, [alignment]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (activeTrackColor) {
      containerRef.current.style.setProperty('--switch-track-checked', activeTrackColor);
      containerRef.current.style.setProperty('--switch-glow-color', activeTrackColor);
    } else {
      containerRef.current.style.removeProperty('--switch-track-checked');
      containerRef.current.style.removeProperty('--switch-glow-color');
    }

    if (inactiveTrackColor) {
      containerRef.current.style.setProperty('--switch-track-unchecked', inactiveTrackColor);
    } else {
      containerRef.current.style.removeProperty('--switch-track-unchecked');
    }
  }, [activeTrackColor, inactiveTrackColor]);

  const handle = (checked) => {
    setCheck(checked);

    if (did) {
      const body = JSON.stringify({
        device: did,
        template: tid,
        data: { [pin]: Number(checked).toString() }
      });

      axios.post(
        `${SENSORS_URL}/control`,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      ).then(
        res => console.log(res.data)
      ).catch(() => setCheck(parseFloat(value) !== 0));
    }
  };

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'switch-widget--grabbing' : 'switch-widget--grab')
    : 'switch-widget--auto';

  return (
    <div
      ref={containerRef}
      className={[
        'widget-container',
        'switch-widget',
        cursorClass,
        transparentBackground ? 'switch-widget--transparent' : '',
      ].filter(Boolean).join(' ')}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(e) => {
        const id = e.target.closest('.widget')?.id;
        if (!id) return;

        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;

        let dragData = `switch;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      <h3 className="switch-widget__title">{title}</h3>

      <div className="switch-widget__row">
        <CustomSwitch checked={check} onChange={handle} />

        {shouldShowLabel ? (
          <span className="switch-widget__label">
            {check ? resolvedOnLabel : resolvedOffLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default Switch;
